import { Suspense, useEffect, useMemo, useRef } from "react"
import { DefaultValues, FormProvider, useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { useFragment } from "react-relay"
import { zodResolver } from "@hookform/resolvers/zod"
import { useUser } from "@/apis/bff"
import { UserAvatarTooltip } from "@/components/avatar-user-tooltip"
import { ScrollWrapper } from "@/components/scroll-wrapper"
import { Banner } from "@/components/ui/banner"
import { Button } from "@/components/ui/button"
import { Card, CardBody, CardHeader } from "@/components/ui/card"
import { Dialog, DialogContentCustom, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { List } from "@/components/ui/list"
import { UserProvider } from "@/components/user-wrapper"
import { FaroInsightsWrapper } from "@/grafana-faro"
import { toast } from "@/hooks/useToast"
import { formatDateString } from "@/lib/format-date"
import { ChConcludeBillingSection } from "@/modules/common/billing/components/billing-form/ch/conclude/ch-conclude-billing-section"
import {
  ChConcludeBillingSchemaType,
  createChConcludeBillingSchema,
} from "@/modules/common/billing/components/billing-form/ch/conclude/schema/ch-conclude-billing-schema"
import {
  DEFAULT_NOT_BILLABLE_REASON,
  EMPLOYER_INSURANCE,
} from "@/modules/common/billing/components/billing-form/constants"
import { DeBillingSchemaType } from "@/modules/common/billing/components/billing-form/de/schema/de-billing-schema"
import { mapBillingFormToCollectInput } from "@/modules/common/billing/components/billing-form/mapBillingFormToCollectInput"
import { BillingAttributeFragment$key } from "@/modules/patients/detail/conclude/billing-section/services/__generated__/BillingAttributeFragment.graphql"
import { BillingAttributeFragment } from "@/modules/patients/detail/conclude/billing-section/services/BillingAttributeFragment"
import { DocumentViewer } from "@/modules/patients/detail/conclude/document-viewer"
import { getEpisodeOfCareFragment } from "@/modules/patients/detail/episode-of-care/lib/get-episode-of-care-fragment"
import { EpisodeOfCareFragment$key } from "@/modules/patients/detail/episode-of-care/types/episode-of-care-fragment-type"
import { CarePlanViewer } from "@/modules/patients/detail/progress-note/documents/care-plan-summary-document-preview"
import ReferralLetterDocument from "@/modules/patients/detail/progress-note/documents/referral-letter-document"
import { TreatmentPlanDocument } from "@/modules/patients/detail/progress-note/documents/treatment-plan-document"
import { ChangeCareProviderResult } from "@/modules/patients/detail/progress-note/hooks/useChangeCareProviderFlow"
import { isDateOlderThan24Hours } from "@/modules/patients/detail/progress-note/lib/is-date-older-than-24-hours"
import { ProgressNoteDocument } from "@/modules/patients/detail/progress-note/types/document"
import { ProgressNoteData } from "@/modules/patients/detail/progress-note/types/progress-note-fragment-type"
import {
  ConcludeDialogStoreProvider,
  useConcludeDialogStoreContext,
} from "@/modules/patients/detail/store/useConcludeDialogStore"
import { useEpisodeOfCareStoreContext } from "@/modules/patients/detail/store/useEpisodeOfCareStore"
import { useAppointmentParam } from "@/modules/patients/hooks/useAppointmentParam"
import { useCountryParam } from "@/modules/patients/hooks/useCountryParam"
import { usePatientParam } from "@/modules/patients/hooks/usePatientParam"
import { getInsureeInfo } from "@/modules/patients/libs/get-insuree-info"
import { GynaecologyFragment$key } from "@/modules/patients/services/__generated__/GynaecologyFragment.graphql"
import { PatientFragment$key } from "@/modules/patients/services/__generated__/PatientFragment.graphql"
import { GynaecologyFragment } from "@/modules/patients/services/GynaecologyFragment"
import { PatientFragment } from "@/modules/patients/services/PatientFragment"
import { refetchChPatientAgenda } from "@/modules/patients/services/usePatientAgenda"
import { usePatientByIdQuery } from "@/modules/patients/services/usePatientByIdQuery"
import { usePatientGynaecology } from "@/modules/patients/services/usePatientGynaecology"
import { useReConcludeAppointmentMutation } from "@/modules/patients/services/useReConcludeAppointmentMutation"
import { CARE_PLAN_SUMMARY } from "@/modules/patients/types/ProgressNoteDocument"
import { Country } from "@/types/__generated__/union-types"

type Props = {
  open: boolean
  onOpenChange: (open: boolean) => void
  progressNote: ProgressNoteData
  changeResult: ChangeCareProviderResult
  onReConcluded?: () => void
}

type BillingSchemaType = DeBillingSchemaType & ChConcludeBillingSchemaType

const ChangeCareProviderReConcludeDialog = (props: Props) => {
  return (
    <Dialog open={props.open} onOpenChange={props.onOpenChange}>
      <ConcludeDialogStoreProvider>
        <DialogContentCustom
          onPointerDownOutside={(e) => e.preventDefault()}
          data-testid="conclude-dialog-content"
          className="flex h-[calc(100vh-120px)] flex-col">
          <Suspense fallback={null}>
            <DialogContent {...props} />
          </Suspense>
        </DialogContentCustom>
      </ConcludeDialogStoreProvider>
    </Dialog>
  )
}

const DialogContent = ({ onOpenChange, progressNote, changeResult, onReConcluded }: Props) => {
  const { t, i18n } = useTranslation()
  const country = useCountryParam()
  const { role } = useUser()
  const isMedicalSupervisor = role === "MedicalSupervisor"
  const appointmentId = progressNote.appointment.id

  const setMarkdownPreviews = useConcludeDialogStoreContext((s) => s.actions.setMarkdownPreviews)
  const setSelectedDocument = useConcludeDialogStoreContext((s) => s.actions.setSelectedDocument)
  const setTriggeredValidation = useConcludeDialogStoreContext((s) => s.actions.setTriggeredValidation)
  const triggeredValidation = useConcludeDialogStoreContext((s) => s.triggeredValidation)

  const patientId = usePatientParam()
  const urlAppointmentId = useAppointmentParam()
  const [patientData] = usePatientByIdQuery(patientId, country, urlAppointmentId)
  const patientFragmentData = useFragment<PatientFragment$key>(PatientFragment, patientData.patientById)
  const patientAge = patientFragmentData?.age?.years
  const patientOrganisationKey =
    getInsureeInfo(patientData.patientById?.insurees)?.coverage?.product?.organisation?.key ?? null
  const gynaecologyRef = usePatientGynaecology(patientId, country)
  const gynaecology = useFragment<GynaecologyFragment$key>(GynaecologyFragment, gynaecologyRef)
  const isPatientPregnant = gynaecology?.pregnancy === "PREGNANT"

  const [reConcludeAppointment, isReConcluding] = useReConcludeAppointmentMutation()
  const hasSelectedInitialDocumentRef = useRef(false)

  const persistedBillingData = useFragment<BillingAttributeFragment$key>(
    BillingAttributeFragment,
    progressNote.appointment.billingAttribute,
  )

  const treatmentPlanNode = useMemo(
    () => progressNote.carePlanSummary?.edges?.[0]?.node ?? null,
    [progressNote.carePlanSummary],
  )
  const referralLetter = progressNote.__typename === "ch_ProgressNote" ? progressNote.referralLetter : null

  const hasUnsignedOrUnApproved = useMemo(() => {
    const docs: Array<{ signedAt: string | null | undefined } | null> = []
    if (progressNote.treatmentPlan) docs.push(treatmentPlanNode)
    if (referralLetter) docs.push(referralLetter)
    return docs.some((doc) => !doc?.signedAt)
  }, [progressNote.treatmentPlan, treatmentPlanNode, referralLetter])

  useEffect(() => {
    if (changeResult.carePlanPreview) {
      setMarkdownPreviews([changeResult.carePlanPreview])
    }
  }, [changeResult.carePlanPreview, setMarkdownPreviews])

  useEffect(() => {
    if (hasSelectedInitialDocumentRef.current) return
    hasSelectedInitialDocumentRef.current = true
    if (treatmentPlanNode && progressNote.treatmentPlan) {
      const { id, fileId, signedAt, signedBy } = treatmentPlanNode
      setSelectedDocument({
        id,
        type: CARE_PLAN_SUMMARY,
        progressNoteId: progressNote.id,
        fileId,
        signedAt,
        signedBy,
        renderFn: () => <CarePlanViewer id={id} progressNoteId={progressNote.id} fileId={fileId} signedAt={signedAt} />,
      })
    } else if (referralLetter) {
      setSelectedDocument({
        id: referralLetter.id,
        type: "referralLetter",
        progressNoteId: progressNote.id,
        fileId: referralLetter.fileId,
        signedAt: referralLetter.signedAt,
        signedBy: referralLetter.signedBy,
      })
    }
  }, [treatmentPlanNode, referralLetter, progressNote.id, progressNote.treatmentPlan, setSelectedDocument])

  const BillingSchema = createChConcludeBillingSchema()

  const isPsychosocialConsultation = progressNote.appointment.service === "PSYCHOSOCIAL"

  const existingBillingData: DefaultValues<BillingSchemaType> | null = persistedBillingData
    ? {
        ...persistedBillingData,
        isNotBillable:
          progressNote.appointment.service === "ADMINISTRATIVE_REQUEST" ? false : persistedBillingData.isNotBillable,
        reason: persistedBillingData.reason ?? undefined,
        treatmentReason:
          persistedBillingData.treatmentReason ??
          (isPsychosocialConsultation && !persistedBillingData.isNotBillable ? "SICKNESS" : undefined),
        notBillableReason: persistedBillingData.notBillableReason ?? DEFAULT_NOT_BILLABLE_REASON,
        isVisualSymptomExamination: persistedBillingData.isVisualSymptomExamination ?? false,
        currentGestationWeek: persistedBillingData.currentGestationWeek
          ? persistedBillingData.currentGestationWeek.toString()
          : null,
        organisationId: persistedBillingData.isEmployer
          ? EMPLOYER_INSURANCE
          : (persistedBillingData.organisationId ?? undefined),
        dateOfAccident: persistedBillingData.dateOfAccident ? new Date(persistedBillingData.dateOfAccident) : undefined,
        accidentReference: persistedBillingData.accidentReference ?? undefined,
        caseReference: persistedBillingData.ivgCaseReference ?? undefined,
      }
    : null

  const form = useForm<BillingSchemaType>({
    mode: "all",
    resolver: zodResolver(BillingSchema),
    defaultValues: existingBillingData ?? {
      isNotBillable: false,
      notBillableReason: DEFAULT_NOT_BILLABLE_REASON,
      reason: "",
      treatmentReason: isPsychosocialConsultation ? "SICKNESS" : undefined,
    },
  })

  const mapToBillingInput = (billingData: BillingSchemaType) =>
    mapBillingFormToCollectInput(billingData, {
      appointmentId,
      country,
      patientAge,
      patientOrganisationKey,
      isPatientPregnant,
    })

  const handleSend = async (billingData: BillingSchemaType) => {
    if (hasUnsignedOrUnApproved) {
      setTriggeredValidation()
      return
    }
    const collectBillingInput = mapToBillingInput(billingData)
    const isEditWithin24Hours = !isDateOlderThan24Hours(progressNote.appointment.completedAt ?? null)
    await reConcludeAppointment({
      id: appointmentId,
      collectBillingInput,
      progressNoteIds: [progressNote.id],
      isEditWithin24Hours,
    })
    setTimeout(() => refetchChPatientAgenda(patientId, i18n.language), 500)
    onReConcluded?.()
    toast({ description: t("patient.conclude.sendMessageSuccessfully") })
    onOpenChange(false)
  }

  const findNextDocument = (currentId: string): ProgressNoteDocument | null => {
    const candidates: ProgressNoteDocument[] = []

    if (progressNote.treatmentPlan && treatmentPlanNode) {
      const { id, fileId, signedAt, signedBy } = treatmentPlanNode
      candidates.push({
        id,
        type: CARE_PLAN_SUMMARY,
        progressNoteId: progressNote.id,
        fileId,
        signedAt,
        signedBy,
        renderFn: () => <CarePlanViewer id={id} progressNoteId={progressNote.id} fileId={fileId} signedAt={signedAt} />,
      })
    }

    if (referralLetter) {
      candidates.push({
        id: referralLetter.id,
        type: "referralLetter",
        progressNoteId: progressNote.id,
        fileId: referralLetter.fileId,
        signedAt: referralLetter.signedAt,
        signedBy: referralLetter.signedBy,
      })
    }

    return candidates.find((doc) => doc.id !== currentId && !doc.signedAt) ?? null
  }

  const handleSuccessfulSigning = (currentId: string) => {
    const next = findNextDocument(currentId)
    if (next) {
      setSelectedDocument(next)
    }
  }

  return (
    <FormProvider {...form}>
      <DialogHeader className="border-b">
        <DialogTitle data-testid="change-care-provider-re-conclude-dialog-title">
          {t("patient.header.conclude")}
        </DialogTitle>
      </DialogHeader>
      <div className="mx-6 flex flex-1 flex-col overflow-hidden">
        {triggeredValidation && hasUnsignedOrUnApproved && (
          <div className="mt-4 mb-8 grid gap-1">
            <Banner type="inline" variant="critical">
              {t("patient.conclude.signOrApproveAllDocuments")}
            </Banner>
          </div>
        )}
        <div className="mt-4 mb-8 grid gap-1">
          <Banner type="inline" variant="info">
            {t("patient.conclude.reEditWarning")}
          </Banner>
        </div>
        <div className="grid flex-1 grid-cols-[373px_763px] gap-3 overflow-hidden">
          <Aside
            progressNote={progressNote}
            isMedicalSupervisor={isMedicalSupervisor}
            country={country}
            onSelectDocument={setSelectedDocument}
          />
          <DocumentViewer onSigningSuccess={handleSuccessfulSigning} keepOriginalAuthor />
        </div>
      </div>
      <DialogFooter className="mt-auto flex border-t p-6 pt-4" data-testid="change-care-provider-re-conclude-footer">
        <Button
          className="px-4 py-2"
          variant="secondary"
          onClick={() => {
            FaroInsightsWrapper.pushEvent("check_out_continue_editing")
            onOpenChange(false)
          }}
          data-testid="continue-editing-button">
          {t("patient.conclude.continueEditing")}
        </Button>
        <Button
          className="px-4 py-2"
          data-testid="send-documents-button"
          isLoading={isReConcluding}
          onClick={() => {
            FaroInsightsWrapper.pushEvent("check_out_send_documents")
            form.handleSubmit(handleSend)()
          }}>
          {t("patient.conclude.sendDocuments")}
        </Button>
      </DialogFooter>
    </FormProvider>
  )
}

type AsideProps = {
  progressNote: ProgressNoteData
  isMedicalSupervisor: boolean
  country: Country
  onSelectDocument: (doc: ProgressNoteDocument) => void
}

const Aside = ({ progressNote, isMedicalSupervisor, country, onSelectDocument }: AsideProps) => {
  const { t } = useTranslation()
  const { userName } = useUser()
  const treatmentPlanNode = progressNote.carePlanSummary?.edges?.[0]?.node ?? null
  const referralLetter = progressNote.__typename === "ch_ProgressNote" ? progressNote.referralLetter : null
  const progressNoteId = progressNote.id
  const { updatedBy, createdAt, appointment, createdBy } = progressNote
  const isTreatingDoctor = createdBy === userName
  const isWithin24HoursAfterClose = !isDateOlderThan24Hours(appointment.completedAt ?? null)

  const parentEpisodeOfCareRef = useEpisodeOfCareStoreContext(
    (state) =>
      state.progressNoteAndParentData.find((item) => item.episodeOfCareProgressNoteData.id === progressNote.id)
        ?.parentEpisodeOfCare ?? null,
  )
  const episodeOfCare = useFragment<EpisodeOfCareFragment$key>(
    getEpisodeOfCareFragment(country),
    parentEpisodeOfCareRef,
  )

  return (
    <ScrollWrapper className="px-1 py-4">
      <div className="grid h-full gap-4" data-testid="change-care-provider-re-conclude-aside">
        {isMedicalSupervisor && isTreatingDoctor && isWithin24HoursAfterClose && (
          <ChConcludeBillingSection
            prefillGestationWeek
            isAdministrativeRequest={appointment.service === "ADMINISTRATIVE_REQUEST"}
          />
        )}
        <Card>
          <CardHeader className="gap-4 pl-4">
            <div className="flex items-center gap-4">
              <UserProvider
                userName={updatedBy}
                render={(user) => <UserAvatarTooltip user={user} uniqueIdentifier={progressNoteId} size="md" />}
              />
              <div>
                <div className="text-on-surface grid items-center gap-1 text-base font-medium">
                  <div>
                    {t(`scheduling.waitingRoom.service.${appointment.service}`)} {t("episodeOfCare.progressNote.for")}
                  </div>
                  <div className="-mt-1">
                    {episodeOfCare
                      ? episodeOfCare.icdDiagnosis.text
                      : t("episodeOfCare.progressNote.unassignedEpisodeOfCare")}
                  </div>
                </div>
                <div className="text-on-surface-subdued text-xs leading-4 font-normal tracking-[0.25px]">
                  {formatDateString(createdAt)}
                </div>
              </div>
            </div>
          </CardHeader>
          <CardBody className="border-t px-4 pb-4">
            {progressNote.treatmentPlan && treatmentPlanNode?.treatmentPlan && (
              <>
                <div className="text-on-surface-subdued my-3.5 text-sm font-medium">
                  {t("patient.conclude.forThePatient")}
                </div>
                <List className="grid gap-2">
                  <TreatmentPlanDocument
                    id={treatmentPlanNode.id}
                    treatmentPlan={treatmentPlanNode.treatmentPlan}
                    isEditable={false}
                    isConclusion
                    isSigned={!!treatmentPlanNode.signedAt}
                    onSelect={() => {
                      const { id, fileId, signedAt, signedBy } = treatmentPlanNode
                      onSelectDocument({
                        id,
                        type: CARE_PLAN_SUMMARY,
                        progressNoteId,
                        fileId,
                        signedAt,
                        signedBy,
                        renderFn: () => (
                          <CarePlanViewer id={id} progressNoteId={progressNoteId} fileId={fileId} signedAt={signedAt} />
                        ),
                      })
                    }}
                  />
                </List>
              </>
            )}
            {referralLetter && (
              <>
                <div className="text-on-surface-subdued my-3.5 text-sm font-medium">
                  {t("patient.conclude.forExternalCareProvider")}
                </div>
                <List className="grid gap-2">
                  <ReferralLetterDocument
                    fragmentRef={referralLetter}
                    progressNoteId={progressNoteId}
                    isEditable={false}
                    isConclusion
                    isSigned={!!referralLetter.signedAt}
                    isSent={!!(referralLetter.concludedAt && referralLetter.concludedBy)}
                    onSelect={() =>
                      onSelectDocument({
                        id: referralLetter.id,
                        type: "referralLetter",
                        progressNoteId,
                        fileId: referralLetter.fileId,
                        signedAt: referralLetter.signedAt,
                        signedBy: referralLetter.signedBy,
                      })
                    }
                  />
                </List>
              </>
            )}
          </CardBody>
        </Card>
      </div>
    </ScrollWrapper>
  )
}

export { ChangeCareProviderReConcludeDialog }
